import { useState, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { getProjectCharacterRegistry } from '../../../services/characterRegistryService';

interface CharacterStatusAnalysis {
  characterId: string;
  characterName: string;
  hasPrompt: boolean;
  hasImage: boolean;
  promptId?: string;
  imageUrl?: string;
  scenePromptsUsingCharacter: string[];
  scenePromptTitles: string[];
}

interface CharacterStatusHookReturn {
  characterStatusAnalysis: CharacterStatusAnalysis[];
  hasAnyMissingSteps: boolean;
  showCharacterModal: boolean;
  setShowCharacterModal: (show: boolean) => void;
  checkCharacterStatus: (prompts: any[], projectId: string) => void;
  getStatusSummary: () => {
    totalCharacters: number;
    charactersNeedingPrompts: number;
    charactersNeedingImages: number;
    scenePromptsAffected: number;
  };
}

export const useCharacterStatus = (): CharacterStatusHookReturn => {
  const [characterStatusAnalysis, setCharacterStatusAnalysis] = useState<CharacterStatusAnalysis[]>([]);
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const { showNotification } = useNotification();

  const checkCharacterStatus = useCallback(async (prompts: any[], projectId: string) => {
    // console.log('🎭 Starting character status analysis for prompts:', prompts.length);

    // Step 1: Find all character prompts by title patterns
    // Look for "character" at the start of the title or in brackets at the start
    const characterPrompts = prompts.filter(prompt => {
      const title = (prompt.title || '').trim().toLowerCase();
      
      // Match patterns like: [Character], [Character 1], Character 2:, etc.
      // But NOT: [Sound Effect 5]: Character sounds...
      const characterPattern = /^(\[character\]?(\s*\d+)?:?|character(\s+\d+)?:)/i;
      return characterPattern.test(title);
    });

    //console.log('📝 Found character prompts by title pattern:', characterPrompts.length);
    //console.log('🔍 Character prompt titles:', characterPrompts.map(p => p.title));

    if (characterPrompts.length === 0) {
      //console.log('✅ No character prompts found');
      setCharacterStatusAnalysis([]);
      return;
    }

    // Step 2: Get project character registry to check for images
    let characterRegistry = {};
    try {
      characterRegistry = await getProjectCharacterRegistry(projectId);
      //console.log('📋 Project character registry:', characterRegistry);
    } catch (error) {
      console.error('❌ Failed to get character registry:', error);
    }

    // Step 3: Analyze each character prompt
    const analysis: CharacterStatusAnalysis[] = [];

    characterPrompts.forEach((prompt, index) => {
      const title = prompt.title || '';
      
      // Extract character number from title, or use index + 1 as fallback
      let charNumber = '1';
      const numberMatch = title.match(/character\s*(\d+)/i) || title.match(/char\s*(\d+)/i);
      if (numberMatch) {
        charNumber = numberMatch[1];
      } else {
        // For titles like "[Character]:" without numbers, use sequential numbering
        charNumber = (index + 1).toString();
      }
      
      const characterId = `CHAR ${charNumber}`;
      const characterName = `Character ${charNumber}`;
      
      // Check if prompt is generated (has content)
      const hasPrompt = !!(prompt.generatedPrompt && prompt.generatedPrompt.trim().length > 0);
        
        // Check if character has image in registry
        const registryEntry = characterRegistry[characterId] || characterRegistry[`CHAR_${charNumber}`] || characterRegistry[`Character ${charNumber}`];
        const hasImage = !!registryEntry?.imageUrl;
        
        // console.log(`🎭 Character ${characterName}:`, {
        //   hasPrompt,
        //   hasImage,
        //   promptId: prompt.id,
        //   registryImageUrl: registryEntry?.imageUrl || 'none'
        // });

        analysis.push({
          characterId,
          characterName,
          hasPrompt,
          hasImage,
          promptId: hasPrompt ? prompt.id : undefined,
          imageUrl: registryEntry?.imageUrl || undefined,
          scenePromptsUsingCharacter: [], // Will be populated if we find scene prompts using this character
          scenePromptTitles: []
        });
    });

    setCharacterStatusAnalysis(analysis);

    // Step 4: Check what's missing
    const charactersNeedingPrompts = analysis.filter(char => !char.hasPrompt);
    const charactersNeedingImages = analysis.filter(char => char.hasPrompt && !char.hasImage);
    const hasAnyMissing = charactersNeedingPrompts.length > 0 || charactersNeedingImages.length > 0;

    // console.log('📊 Character Analysis Summary:', {
    //   totalCharacters: analysis.length,
    //   charactersNeedingPrompts: charactersNeedingPrompts.length,
    //   charactersNeedingImages: charactersNeedingImages.length,
    //   hasAnyMissing,
    //   charactersWithMissingPrompts: charactersNeedingPrompts.map(c => c.characterName),
    //   charactersWithMissingImages: charactersNeedingImages.map(c => c.characterName)
    // });

    // Step 5: Show notification if there are missing elements
    if (hasAnyMissing) {
      //console.log('🚨 Triggering character notification for missing elements');
      
      // Auto-show notification after brief delay
      setTimeout(() => {
        //console.log('🎭 Showing character notification now!');
        setShowCharacterModal(true);
      }, 1000);
    } else {
      //console.log('✅ All characters have prompts and images - character consistency is complete!');
    }
  }, [showNotification]);

  const getStatusSummary = () => {
    const totalCharacters = characterStatusAnalysis.length;
    const charactersNeedingPrompts = characterStatusAnalysis.filter(char => !char.hasPrompt).length;
    const charactersNeedingImages = characterStatusAnalysis.filter(char => char.hasPrompt && !char.hasImage).length;
    const scenePromptsAffected = new Set(
      characterStatusAnalysis.flatMap(char => char.scenePromptsUsingCharacter)
    ).size;

    return {
      totalCharacters,
      charactersNeedingPrompts,
      charactersNeedingImages,
      scenePromptsAffected
    };
  };

  const hasAnyMissingSteps = characterStatusAnalysis.some(char => !char.hasPrompt || !char.hasImage);

  return {
    characterStatusAnalysis,
    hasAnyMissingSteps,
    showCharacterModal,
    setShowCharacterModal,
    checkCharacterStatus,
    getStatusSummary
  };
};
